/**
 * CreateBlueprintModal â€” Excelencia Module
 *
 * Modal para criacao de blueprint com 3 origens:
 *   1. Criar do zero â€” definicao manual de etapas (com editor completo)
 *   2. Importar de Mapa â€” selecionar mapa VSM existente
 *   3. Importar de Workflow â€” selecionar workflow template V9
 *
 * Stages no modo "Criar do Zero" usam BlueprintStageEditor (accordion colapsavel)
 * com suporte a checklist, formFields, instruction, completionType, exitOptions.
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  X,
  Plus,
  Trash2,
  Loader2,
  FileText,
  Map,
  GitBranch,
  PenTool,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  AlertCircle,
} from 'lucide-react';
import { CATEGORY_LABELS } from '../../../types/processBlueprint';
import type { BlueprintCategory, BlueprintStage } from '../../../types/processBlueprint';
import type {
  MapSourceItem,
  WorkflowSourceItem,
} from '../services/excelenciaService';
import { BlueprintStageEditor } from '../components/BlueprintStageEditor';

// ============================================================================
// Types
// ============================================================================

type BlueprintOrigin = 'scratch' | 'map' | 'workflow';

export interface CreateBlueprintResult {
  origin: BlueprintOrigin;
  name: string;
  description?: string;
  category?: string;
  /** Only for origin='scratch' */
  stages?: BlueprintStage[];
  /** Only for origin='map' */
  mapId?: string;
  /** Only for origin='workflow' */
  templateId?: string;
}

interface CreateBlueprintModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (params: CreateBlueprintResult) => Promise<void>;
  /** Load available maps for import */
  loadMaps?: () => Promise<MapSourceItem[]>;
  /** Load available workflow templates for import */
  loadWorkflows?: () => Promise<WorkflowSourceItem[]>;
}

// ============================================================================
// Constants
// ============================================================================

const CATEGORY_OPTIONS = Object.entries(CATEGORY_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const ORIGIN_OPTIONS: Array<{
  value: BlueprintOrigin;
  label: string;
  description: string;
  icon: React.ReactNode;
}> = [
  {
    value: 'scratch',
    label: 'Criar do Zero',
    description: 'Defina etapas manualmente',
    icon: <PenTool size={18} />,
  },
  {
    value: 'map',
    label: 'Importar de Mapa',
    description: 'Extrair etapas de um mapa VSM',
    icon: <Map size={18} />,
  },
  {
    value: 'workflow',
    label: 'Importar de Workflow',
    description: 'Importar stages de um template',
    icon: <GitBranch size={18} />,
  },
];

// ============================================================================
// Helpers
// ============================================================================

function createEmptyStage(order: number): BlueprintStage {
  return {
    order,
    name: '',
    description: '',
    slaHours: undefined,
    completionType: 'simple_complete',
  };
}

// ============================================================================
// Component
// ============================================================================

export const CreateBlueprintModal: React.FC<CreateBlueprintModalProps> = React.memo(
  ({ open, onClose, onCreate, loadMaps, loadWorkflows }) => {
    // -- Form state --
    const [origin, setOrigin] = useState<BlueprintOrigin>('scratch');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('geral');
    const [stages, setStages] = useState<BlueprintStage[]>([createEmptyStage(1)]);
    const [expandedStageIndex, setExpandedStageIndex] = useState<number | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // -- Import sources --
    const [maps, setMaps] = useState<MapSourceItem[]>([]);
    const [workflows, setWorkflows] = useState<WorkflowSourceItem[]>([]);
    const [selectedMapId, setSelectedMapId] = useState<string>('');
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
    const [isLoadingSources, setIsLoadingSources] = useState(false);

    // -- Load sources on origin change --
    useEffect(() => {
      if (!open) return;

      if (origin === 'map' && maps.length === 0 && loadMaps) {
        setIsLoadingSources(true);
        setError(null);
        loadMaps()
          .then(setMaps)
          .catch((err: unknown) => {
            setMaps([]);
            setError(err instanceof Error ? err.message : 'Não foi possível carregar os mapas');
          })
          .finally(() => setIsLoadingSources(false));
      }

      if (origin === 'workflow' && workflows.length === 0 && loadWorkflows) {
        setIsLoadingSources(true);
        loadWorkflows()
          .then(setWorkflows)
          .catch(() => setWorkflows([]))
          .finally(() => setIsLoadingSources(false));
      }
    }, [origin, open, loadMaps, loadWorkflows, maps.length, workflows.length]);

    // -- Auto-fill name from selected source --
    useEffect(() => {
      if (origin === 'map' && selectedMapId) {
        const selected = maps.find(m => m.id === selectedMapId);
        if (selected && !name.trim()) {
          setName(selected.name);
        }
      }
    }, [selectedMapId, origin, maps, name]);

    useEffect(() => {
      if (origin === 'workflow' && selectedTemplateId) {
        const selected = workflows.find(w => w.id === selectedTemplateId);
        if (selected && !name.trim()) {
          setName(selected.name);
        }
      }
    }, [selectedTemplateId, origin, workflows, name]);

    const resetForm = useCallback(() => {
      setOrigin('scratch');
      setName('');
      setDescription('');
      setCategory('geral');
      setStages([createEmptyStage(1)]);
      setExpandedStageIndex(null);
      setSelectedMapId('');
      setSelectedTemplateId('');
      setError(null);
      setMaps([]);
      setWorkflows([]);
    }, []);

    const handleClose = useCallback(() => {
      resetForm();
      onClose();
    }, [onClose, resetForm]);

    // -- Stage management (scratch mode) --
    const addStage = useCallback(() => {
      setStages(prev => [...prev, createEmptyStage(prev.length + 1)]);
      setExpandedStageIndex(stages.length);
    }, [stages.length]);

    const removeStage = useCallback((index: number) => {
      setStages(prev =>
        prev
          .filter((_, i) => i !== index)
          .map((s, i) => ({ ...s, order: i + 1 })),
      );
      setExpandedStageIndex(null);
    }, []);

    const updateStage = useCallback((index: number, updated: BlueprintStage) => {
      setStages(prev => prev.map((s, i) => (i === index ? { ...updated, order: i + 1 } : s)));
    }, []);

    const toggleStageExpansion = useCallback((index: number) => {
      setExpandedStageIndex(prev => (prev === index ? null : index));
    }, []);

    // -- Submit --
    const handleSubmit = useCallback(async () => {
      if (!name.trim()) {
        setError('Nome do blueprint e obrigatorio');
        return;
      }

      if (name.trim().length < 5) {
        setError('Nome muito curto. Use ao menos 5 caracteres (ex: "Onboarding de Clientes")');
        return;
      }

      if (origin === 'map' && !selectedMapId) {
        setError('Selecione um mapa para importar');
        return;
      }

      if (origin === 'workflow' && !selectedTemplateId) {
        setError('Selecione um workflow para importar');
        return;
      }

      const result: CreateBlueprintResult = {
        origin,
        name: name.trim(),
        description: description.trim() || undefined,
        category: category || 'geral',
      };

      if (origin === 'scratch') {
        const validStages = stages
          .filter(s => s.name.trim())
          .map((s, i) => ({ ...s, order: i + 1 }));
        result.stages = validStages.length > 0 ? validStages : undefined;
      } else if (origin === 'map') {
        result.mapId = selectedMapId;
      } else if (origin === 'workflow') {
        result.templateId = selectedTemplateId;
      }

      setIsSubmitting(true);
      setError(null);
      try {
        await onCreate(result);
        resetForm();
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Erro ao criar blueprint');
      } finally {
        setIsSubmitting(false);
      }
    }, [name, description, category, origin, stages, selectedMapId, selectedTemplateId, onCreate, resetForm]);

    if (!open) return null;

    const submitLabel =
      origin === 'scratch'
        ? 'Criar Blueprint'
        : origin === 'map'
          ? 'Importar de Mapa'
          : 'Importar de Workflow';

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="w-full max-w-3xl bg-white dark:bg-[var(--bg-surface)] rounded shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden flex flex-col" style={{ maxHeight: '90vh' }}>
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-slate-800 shrink-0">
            <div className="flex items-center gap-2">
              <FileText size={18} className="text-purple-500" />
              <h2 className="text-sm font-bold text-gray-900 dark:text-slate-100">
                Novo Blueprint
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-slate-800 rounded transition-colors"
            >
              <X size={16} className="text-gray-500" />
            </button>
          </div>

          {/* Form */}
          <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1 min-h-0">
            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2">
                <AlertCircle size={14} className="shrink-0" />
                {error}
              </div>
            )}

            {/* --- ORIGIN SELECTOR --- */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-2">
                Origem
              </label>
              <div className="grid grid-cols-3 gap-2">
                {ORIGIN_OPTIONS.map(opt => {
                  const isSelected = origin === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        setOrigin(opt.value);
                        setError(null);
                      }}
                      className={`
                        flex flex-col items-center gap-1.5 p-3 rounded border-2 transition-all text-center
                        ${isSelected
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                          : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600 text-gray-600 dark:text-slate-400'
                        }
                      `}
                    >
                      <div className={isSelected ? 'text-purple-500' : 'text-gray-400 dark:text-slate-500'}>
                        {opt.icon}
                      </div>
                      <span className="text-[11px] font-semibold leading-tight">{opt.label}</span>
                      <span className="text-[10px] leading-tight opacity-70">{opt.description}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* --- NAME --- */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1">
                Nome do Blueprint *
              </label>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Ex: Processo de Onboarding"
                className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded focus:outline-none focus:ring-2 focus:ring-purple-500/30 text-gray-900 dark:text-slate-100 placeholder-gray-400"
                autoFocus
              />
            </div>

            {/* --- CATEGORY --- */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1">
                Categoria
              </label>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded focus:outline-none focus:ring-2 focus:ring-purple-500/30 text-gray-900 dark:text-slate-100"
              >
                {CATEGORY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* --- DESCRIPTION --- */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1">
                Descricao
              </label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Descrição do processo (obrigatória para publicar)"
                rows={2}
                className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded focus:outline-none focus:ring-2 focus:ring-purple-500/30 text-gray-900 dark:text-slate-100 placeholder-gray-400 resize-none"
              />
            </div>

            {/* ======= ORIGIN-SPECIFIC CONTENT ======= */}

            {/* --- SCRATCH: Stage Editor (Accordion) --- */}
            {origin === 'scratch' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-gray-700 dark:text-slate-300">
                    Etapas do Processo
                  </label>
                  <button
                    onClick={addStage}
                    className="flex items-center gap-1 text-[11px] text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium"
                  >
                    <Plus size={12} />
                    Adicionar Etapa
                  </button>
                </div>

                <div className="space-y-2">
                  {stages.map((stage, idx) => {
                    const isExpanded = expandedStageIndex === idx;
                    return (
                      <div
                        key={idx}
                        className="rounded border overflow-hidden bg-gray-50 dark:bg-slate-800/50 border-gray-200 dark:border-slate-700"
                      >
                        {/* Stage header (always visible) */}
                        <div
                          className="flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700/30 transition-colors"
                          onClick={() => toggleStageExpansion(idx)}
                        >
                          <span className="text-[10px] font-mono text-gray-400 dark:text-slate-500 w-5 shrink-0 text-center">
                            {idx + 1}.
                          </span>
                          <span className={`flex-1 text-xs font-medium truncate ${
                            stage.name
                              ? 'text-gray-900 dark:text-slate-100'
                              : 'text-gray-400 dark:text-slate-500 italic'
                          }`}>
                            {stage.name || 'Etapa sem nome'}
                          </span>
                          {stage.slaHours != null && stage.slaHours > 0 && (
                            <span className="text-[10px] text-gray-400 dark:text-slate-500 font-mono shrink-0">
                              {stage.slaHours}h
                            </span>
                          )}
                          {stage.completionType === 'approve_reject' && (
                            <span className="text-[9px] bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded font-medium shrink-0">
                              Aprovacao
                            </span>
                          )}
                          {((stage.formFields?.length ?? 0) > 0 || (stage.checklist?.length ?? 0) > 0) && (
                            <span className="text-[9px] bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 px-1.5 py-0.5 rounded font-medium shrink-0">
                              {[
                                (stage.formFields?.length ?? 0) > 0 ? `${stage.formFields!.length} campos` : '',
                                (stage.checklist?.length ?? 0) > 0 ? `${stage.checklist!.length} checks` : '',
                              ].filter(Boolean).join(' + ')}
                            </span>
                          )}
                          <button
                            onClick={e => { e.stopPropagation(); removeStage(idx); }}
                            className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors shrink-0"
                            title="Remover etapa"
                          >
                            <Trash2 size={12} />
                          </button>
                          {isExpanded ? (
                            <ChevronUp size={14} className="text-gray-400 dark:text-slate-500 shrink-0" />
                          ) : (
                            <ChevronDown size={14} className="text-gray-400 dark:text-slate-500 shrink-0" />
                          )}
                        </div>

                        {/* Stage editor (expanded) */}
                        {isExpanded && (
                          <div className="border-t border-gray-200 dark:border-slate-700 px-3 py-3">
                            <BlueprintStageEditor
                              stage={stage}
                              onChange={updated => updateStage(idx, updated)}
                              readOnly={false}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {stages.length === 0 && (
                  <div className="text-center py-4">
                    <p className="text-xs text-gray-400 dark:text-slate-500">
                      Nenhuma etapa adicionada. Clique em "Adicionar Etapa" acima.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* --- MAP: Project/Map selector --- */}
            {origin === 'map' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-2">
                  Selecionar Mapa VSM
                </label>

                {isLoadingSources ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 size={20} className="animate-spin text-purple-500 mr-2" />
                    <span className="text-xs text-gray-500 dark:text-slate-400">Carregando mapas...</span>
                  </div>
                ) : maps.length === 0 ? (
                  <div className="text-center py-6 bg-gray-50 dark:bg-slate-800/50 rounded border border-dashed border-gray-300 dark:border-slate-700">
                    <Map size={24} className="mx-auto text-gray-300 dark:text-slate-600 mb-2" />
                    <p className="text-xs text-gray-500 dark:text-slate-400">
                      Nenhum mapa VSM com atividades encontrado.
                    </p>
                    <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-1">
                      Crie um mapa com atividades no modulo Mapa primeiro.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {maps.map(m => {
                      const isSelected = selectedMapId === m.id;
                      return (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => setSelectedMapId(m.id)}
                          className={`
                            w-full flex items-center gap-3 p-3 rounded border transition-all text-left
                            ${isSelected
                              ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                              : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
                            }
                          `}
                        >
                          <div className={`p-1.5 rounded ${isSelected ? 'bg-purple-100 dark:bg-purple-900/40' : 'bg-gray-100 dark:bg-slate-800'}`}>
                            <Map size={14} className={isSelected ? 'text-purple-500' : 'text-gray-400 dark:text-slate-500'} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-gray-900 dark:text-slate-100 truncate">
                              {m.name}
                            </div>
                            <div className="text-[10px] text-gray-500 dark:text-slate-400">
                              {m.cardCount} atividade{m.cardCount !== 1 ? 's' : ''}
                            </div>
                          </div>
                          {isSelected && (
                            <ChevronRight size={14} className="text-purple-500 shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* --- WORKFLOW: Template selector --- */}
            {origin === 'workflow' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-2">
                  Selecionar Workflow Template
                </label>

                {isLoadingSources ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 size={20} className="animate-spin text-purple-500 mr-2" />
                    <span className="text-xs text-gray-500 dark:text-slate-400">Carregando workflows...</span>
                  </div>
                ) : workflows.length === 0 ? (
                  <div className="text-center py-6 bg-gray-50 dark:bg-slate-800/50 rounded border border-dashed border-gray-300 dark:border-slate-700">
                    <GitBranch size={24} className="mx-auto text-gray-300 dark:text-slate-600 mb-2" />
                    <p className="text-xs text-gray-500 dark:text-slate-400">
                      Nenhum workflow template ativo encontrado.
                    </p>
                    <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-1">
                      Crie e ative um template no modulo Workflow primeiro.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {workflows.map(w => {
                      const isSelected = selectedTemplateId === w.id;
                      return (
                        <button
                          key={w.id}
                          type="button"
                          onClick={() => setSelectedTemplateId(w.id)}
                          className={`
                            w-full flex items-center gap-3 p-3 rounded border transition-all text-left
                            ${isSelected
                              ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                              : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
                            }
                          `}
                        >
                          <div className={`p-1.5 rounded ${isSelected ? 'bg-purple-100 dark:bg-purple-900/40' : 'bg-gray-100 dark:bg-slate-800'}`}>
                            <GitBranch size={14} className={isSelected ? 'text-purple-500' : 'text-gray-400 dark:text-slate-500'} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-gray-900 dark:text-slate-100 truncate">
                              {w.name}
                            </div>
                            <div className="text-[10px] text-gray-500 dark:text-slate-400">
                              {w.stageCount} etapa{w.stageCount !== 1 ? 's' : ''}
                              {w.description ? ` Â· ${w.description}` : ''}
                            </div>
                          </div>
                          {isSelected && (
                            <ChevronRight size={14} className="text-purple-500 shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Import info banner */}
            {origin !== 'scratch' && (
              <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                <AlertCircle size={14} className="text-blue-500 mt-0.5 shrink-0" />
                <div className="text-[11px] text-blue-700 dark:text-blue-300">
                  <p className="font-medium mb-0.5">Importacao com snapshot</p>
                  <p className="opacity-80">
                    {origin === 'map'
                      ? 'As atividades do mapa serao extraidas e salvas como etapas do blueprint. Apos a criacao, voce podera revisar e editar as etapas importadas.'
                      : 'As etapas do workflow template serao importadas como etapas do blueprint. Apos a criacao, voce podera revisar e editar as etapas importadas.'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t border-gray-200 dark:border-slate-800 flex justify-end gap-2 shrink-0">
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-xs text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 rounded transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                !name.trim() ||
                (origin === 'map' && !selectedMapId) ||
                (origin === 'workflow' && !selectedTemplateId)
              }
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 rounded transition-colors disabled:opacity-50"
            >
              {isSubmitting ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Plus size={12} />
              )}
              {submitLabel}
            </button>
          </div>
        </div>
      </div>
    );
  },
);

CreateBlueprintModal.displayName = 'CreateBlueprintModal';
